import { observer } from 'mobx-react-lite';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { useCapacityStore } from '../store/CapacityPlannerContext';
import styles from './cpHeadcountGrid.module.scss';

const fmt = (v: number | undefined): string => {
  if (v === undefined || v === null) return '--';
  return Math.ceil(v).toLocaleString();
};

type RowType = 'roomtype' | 'total' | 'jobs';

interface GridRow {
  id: string;
  label: string;
  rowType: RowType;
  lineHours: number | undefined;
  pa: number | undefined;
  mo: number | undefined;
  qi: number | undefined;
  total: number | undefined;
}

const ROOM_TYPE_ORDER = [
  'High Speed 1',
  'High Speed 2',
  'Medium Speed',
  'Low Speed 1',
  'Low Speed 2',
  'Overlay',
];

const getRowClassName = (row: GridRow): string => {
  if (row.rowType === 'total') return 'row-total';
  if (row.rowType === 'jobs') return 'row-jobs';
  return '';
};

const formatMonthLabel = (m: string): string => {
  const [yr, mn] = m.split('-');
  const d = new Date(parseInt(yr), parseInt(mn) - 1);
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

interface CPHeadcountGridProps {
  selectedMonth: string;
}

const CPHeadcountGrid = observer(({ selectedMonth }: CPHeadcountGridProps) => {
  const store = useCapacityStore();
  const data = store.dashboardData;
  if (!data) return null;

  const { rooms, grand_total, forecast_jobs } = data;
  const m = selectedMonth;

  // Aggregate by room_type
  const byType: Record<string, { lineHours: number; pa: number; mo: number; qi: number; total: number }> = {};

  rooms.forEach((room) => {
    const rt = room.room_type ?? 'Other';
    if (!byType[rt]) byType[rt] = { lineHours: 0, pa: 0, mo: 0, qi: 0, total: 0 };
    const d = room.months[m];
    if (!d) return;
    byType[rt].lineHours += d.line_hours;
    byType[rt].pa       += d.pa_hc;
    byType[rt].mo       += d.mo_hc;
    byType[rt].qi       += d.qi_hc;
    byType[rt].total    += d.total_hc;
  });

  const gridRows: GridRow[] = [];

  // Room type rows in defined order
  ROOM_TYPE_ORDER.forEach((rt) => {
    const agg = byType[rt];
    if (!agg) return; // skip if no active rooms of this type
    gridRows.push({
      id:        `rt_${rt}`,
      label:     rt,
      rowType:   'roomtype',
      lineHours: agg.lineHours,
      pa:        agg.pa,
      mo:        agg.mo,
      qi:        agg.qi,
      total:     agg.total,
    });
  });

  // Any room types not in the order list (catch-all)
  Object.keys(byType).forEach((rt) => {
    if (!ROOM_TYPE_ORDER.includes(rt)) {
      const agg = byType[rt];
      gridRows.push({
        id: `rt_${rt}`, label: rt, rowType: 'roomtype',
        lineHours: agg.lineHours, pa: agg.pa, mo: agg.mo, qi: agg.qi, total: agg.total,
      });
    }
  });

  const gt = grand_total[m] ?? { line_hours: 0, pa_hc: 0, mo_hc: 0, qi_hc: 0, total_hc: 0 };
  gridRows.push({
    id: 'grand_total', label: 'GRAND TOTAL', rowType: 'total',
    lineHours: gt.line_hours, pa: gt.pa_hc, mo: gt.mo_hc, qi: gt.qi_hc, total: gt.total_hc,
  });

  gridRows.push({
    id: 'forecast_jobs', label: 'Forecast Jobs', rowType: 'jobs',
    lineHours: undefined, pa: undefined, mo: undefined, qi: undefined,
    total: forecast_jobs[m],
  });

  const cellStyle: React.CSSProperties = { textAlign: 'right', fontSize: '0.85rem', padding: '0.5rem 0.75rem' };
  const boldCellStyle: React.CSSProperties = { ...cellStyle, fontWeight: 700 };

  const activeTypeCount = Object.keys(byType).length;

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <h2 className={styles.title}>
          <i className="pi pi-table" />
          Headcount Plan
        </h2>
        <span className={styles.meta}>
          {formatMonthLabel(m)} | {activeTypeCount} room type{activeTypeCount !== 1 ? 's' : ''}
        </span>
      </div>
      <DataTable
        value={gridRows}
        size="small"
        dataKey="id"
        rowClassName={(row: GridRow) => getRowClassName(row)}
        showGridlines
        scrollable
        scrollHeight="520px"
      >
        <Column
          field="label"
          header="Room Type"
          style={{ minWidth: '180px', fontWeight: 600, fontSize: '0.85rem', background: '#f8fafc' }}
        />
        <Column header="Line-Hrs" body={(row: GridRow) => fmt(row.lineHours)} style={cellStyle} />
        <Column header="PA"       body={(row: GridRow) => fmt(row.pa)}        style={cellStyle} />
        <Column header="MO"       body={(row: GridRow) => fmt(row.mo)}        style={cellStyle} />
        <Column header="QI"       body={(row: GridRow) => fmt(row.qi)}        style={cellStyle} />
        <Column header="Total"    body={(row: GridRow) => fmt(row.total)}     style={boldCellStyle} />
      </DataTable>
    </div>
  );
});

export default CPHeadcountGrid;
